import { useState, useEffect } from "react";
import ThemeToggle from "./ThemeToggle";
import ThemeChoiceDialog from "./ThemeChoiceDialog";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import ProfileModal from "./ProfileModal";
import AssistantChatWidget from "./AssistantChatWidget";
import { PushNotificationPrompt } from "./PushNotifications";
import { PageHeaderProvider, usePageHeader } from "../context/PageHeaderContext";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Settings,
  LayoutDashboard,
  FileText,
  Bed,
  Users,
  Building2,
  UserCog,
  PiggyBank,
  Menu,
  X,
  Home,
  FolderOpen,
  Bell,
  ShoppingCart,
  ClipboardList,
  UserCheck,
  AlertTriangle,
  Newspaper,
  Receipt,
  Package,
  Headphones,
  BookOpen,
  NotebookPen,
  FileBox,
  Shield,
  Send,
  Loader2,
  CheckCircle2,
  Info,
  Pill,
  HeartPulse,
  Sparkles,
  CalendarDays,
  Bus,
  Truck,
  BarChart3,
  UserSearch,
  Wrench,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import StrutturaSwitcher from "./StrutturaSwitcher";
import { canAccessFatturazioneElettronica } from "../utils/fatturazioneAccess";
import { canAccessDeblistering } from "../utils/deblisteringAccess";
import { canAccessCentroDiurno } from "../utils/centroDiurnoAccess";
import { canAccessImmobiliare } from "../utils/immobiliareAccess";
import { canAccessTrovaOspiti, canAccessTrovaPersonale } from "../utils/trovaServiziAccess";
import { canAccessManutenzioni } from "../utils/manutenzioniAccess";
import { canAccessFamily } from "../utils/familyAccess";
import { toast } from "sonner";
import TermsReacceptanceGate from "./TermsReacceptanceGate";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const LOGO_URL = "/images/logo.png";
const LOGO_FULL_URL = "/images/logo-full.png";
const LOGO_MARK_URL = LOGO_URL;

// Initial alerts data - removed test notifications, only dynamic ones will be shown
const INITIAL_ALERTS = [];

// Struttura menu a sezioni. Le sezioni con `title === null` non mostrano
// intestazione (usata per il gruppo Dashboard in cima).
// requiredPermission: stringa (singolo permesso) che l'utente deve avere per vedere la voce.
// Omesso/null = sempre visibile.
const getNavSections = (user, hasPermission) => {
  const isFattBeta = canAccessFatturazioneElettronica(user);
  const isDeblist = canAccessDeblistering(user);
  const isCentroDiurno = canAccessCentroDiurno(user);
  const isImmobiliare = canAccessImmobiliare(user);
  const isTrovaOspiti = canAccessTrovaOspiti(user);
  const isTrovaPersonale = canAccessTrovaPersonale(user);
  const isManutenzioni = canAccessManutenzioni(user);
  const isFamily = canAccessFamily(user);

  const filterByPermission = (items) =>
    items.filter(item =>
      !item.requiredPermission || hasPermission(item.requiredPermission)
    );

  const anteprimaItems = [
    ...(isTrovaOspiti
      ? [{ path: "/trova-ospiti", label: "Trova Ospiti", icon: Users, badge: "BETA" }]
      : []),
    ...(isTrovaPersonale
      ? [{ path: "/trova-personale", label: "Trova Personale", icon: UserSearch, badge: "BETA" }]
      : []),
  ];

  const sections = [
    {
      title: null,
      items: [{ path: "/", label: "Dashboard", icon: LayoutDashboard }],
    },
    ...(anteprimaItems.length
      ? [
          {
            title: "SERVIZI IN ANTEPRIMA",
            items: anteprimaItems,
          },
        ]
      : []),
    {
      title: "OPERATIVITÀ",
      items: [
        { path: "/alloggiati-web", label: "Alloggiati Web", icon: FileText, requiredPermission: "fascicolo:read" },
        { path: "/posti-liberi", label: "Camere e posti letto", icon: Bed, requiredPermission: "struttura:read" },
        { path: "/personale", label: "Personale", icon: UserCog, requiredPermission: "users:read" },
        ...(isManutenzioni
          ? [{ path: "/manutenzioni", label: "Manutenzioni", icon: Wrench }]
          : []),
      ],
    },
    ...(isCentroDiurno
      ? [
          {
            title: "CENTRO DIURNO",
            items: [
              { path: "/centro-diurno", label: "Dashboard Centro Diurno", icon: Sparkles, requiredPermission: "centro_diurno:read" },
              { path: "/centro-diurno/agenda", label: "Agenda e Prenotazioni", icon: CalendarDays, requiredPermission: "centro_diurno:read" },
              { path: "/centro-diurno/presenze", label: "Presenze giornaliere", icon: UserCheck, requiredPermission: "centro_diurno:read" },
              { path: "/centro-diurno/trasporti", label: "Trasporti e Pulmino", icon: Bus, requiredPermission: "centro_diurno:read" },
              { path: "/centro-diurno/mezzi", label: "Mezzi e Autisti", icon: Truck, requiredPermission: "centro_diurno:read" },
              { path: "/centro-diurno/pacchetti", label: "Pacchetti e Servizi", icon: Package, requiredPermission: "centro_diurno:read" },
              { path: "/centro-diurno/report", label: "Report Centro Diurno", icon: BarChart3, requiredPermission: "centro_diurno:read" },
            ],
          },
        ]
      : []),
    {
      title: "GESTIONE",
      items: [
        { path: "/fascicoli-assistenziali", label: "Fascicoli Assistenziali", icon: FolderOpen, requiredPermission: "fascicolo:read" },
        { path: "/diario-consegne", label: "Diario Consegne", icon: BookOpen, requiredPermission: "diario:read" },
        { path: "/diario-personale", label: "Diario Personale", icon: NotebookPen, requiredPermission: "diario_personale:read" },
        { path: "/documenti", label: "Documenti", icon: FileBox, requiredPermission: "documento:read" },
        { path: "/comunicazioni", label: "Comunicazioni", icon: Send, requiredPermission: "comunicazione:read" },
        ...(isFamily
          ? [{ path: "/family-struttura", label: "AFFIDYA Family", icon: HeartPulse, requiredPermission: "fascicolo:read" }]
          : []),
      ],
    },
    {
      title: "CONTABILITÀ",
      items: [
        ...(isFattBeta ? [
          { path: "/fatturazione", label: "Fatturazione", icon: Receipt, requiredPermission: "billing:read" },
        ] : []),
        { path: "/prima-nota-ospiti", label: "Prima Nota", icon: ClipboardList, requiredPermission: "billing:read" },
        ...(isDeblist ? [{ path: "/deblistering", label: "Deblistering", icon: Package }] : []),
      ],
    },
    {
      title: "BUSINESS",
      items: [
        ...(isImmobiliare ? [{ path: "/immobiliare", label: "Immobiliare", icon: Building2 }] : []),
        { path: "/ospiti-in-rete", label: "Ospiti in Rete", icon: Users },
        { path: "/risparmio", label: "Risparmio insieme", icon: PiggyBank },
      ],
    },
    {
      title: "SISTEMA",
      items: [
        ...(!isFattBeta ? [{ path: "/newsletter", label: "Newsletter", icon: Newspaper, requiredPermission: "admin:read" }] : []),
        { path: "/impostazioni", label: "Impostazioni", icon: Settings },
        { path: "/novita", label: "Novità aggiornamenti", icon: Sparkles },
      ],
    },
  ];

  return sections.map(section => ({
    ...section,
    items: filterByPermission(section.items),
  }));
};

const isCentroDiurnoNavSection = (section) => section.title === "CENTRO DIURNO";

const getNavSectionWrapperClass = (section, sIdx, variant = "desktop") => {
  const spacing = variant === "mobile"
    ? (sIdx > 0 ? "mt-4" : "")
    : (sIdx > 0 ? "mt-2.5" : "");
  if (isCentroDiurnoNavSection(section) && variant !== "collapsed") {
    return `${spacing} rounded-xl bg-slate-300/45 p-2`.trim();
  }
  return spacing;
};

const SUPPORT_CATEGORIE = [
  { value: "tecnico", label: "Problema tecnico" },
  { value: "fatturazione", label: "Fatturazione e pagamenti" },
  { value: "funzionalita", label: "Nuova funzionalità" },
  { value: "alloggiati", label: "Alloggiati Web" },
  { value: "altro", label: "Altro" },
];

function SidebarSupportWidget({ token, user }) {
  const [formOpen, setFormOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [formData, setFormData] = useState({ categoria: "", oggetto: "", messaggio: "", telefono: "" });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.categoria || !formData.messaggio) {
      toast.error("Compila categoria e messaggio");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/assistenza/richiesta`, {
        ...formData,
        user_email: user?.email,
        user_name: user?.name,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSent(true);
      setFormOpen(false);
      toast.success("Richiesta inviata!");
    } catch {
      toast.error("Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setSent(false);
    setFormData({ categoria: "", oggetto: "", messaggio: "", telefono: "" });
  };

  return (
    <div className="p-3 border-t border-slate-200">
      {/* Card sempre visibile, in stile screenshot */}
      <div className="rounded-xl bg-white/60 border border-slate-200 px-4 py-4 text-center">
        {sent ? (
          <div className="flex flex-col items-center gap-2 py-1">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            <p className="text-xs font-medium text-slate-700">Richiesta inviata!</p>
            <Link
              to="/assistenza"
              className="inline-flex items-center justify-center w-full h-8 rounded-lg text-xs font-semibold bg-[#0F766E] hover:bg-[#0d6560] text-white transition-colors"
            >
              Vedi le richieste
            </Link>
            <button onClick={handleReset} className="text-xs underline text-slate-500 hover:text-slate-800">
              Invia un&apos;altra
            </button>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center gap-2 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                <Headphones className="w-5 h-5 text-white" />
              </div>
              <p className="text-sm font-semibold text-slate-800 leading-tight">
                Hai bisogno di aiuto?
              </p>
              <p className="text-[11px] text-slate-500 leading-snug">
                Il nostro team è a tua disposizione.
              </p>
            </div>

            {formOpen && (
              <form onSubmit={handleSubmit} className="space-y-2 mb-3 text-left">
                <Select
                  value={formData.categoria}
                  onValueChange={(v) => setFormData({ ...formData, categoria: v })}
                >
                  <SelectTrigger className="h-8 text-xs bg-white border-slate-300 text-slate-700 focus:ring-0">
                    <SelectValue placeholder="Categoria *" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORT_CATEGORIE.map((c) => (
                      <SelectItem key={c.value} value={c.value} className="text-xs">{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  value={formData.oggetto}
                  onChange={(e) => setFormData({ ...formData, oggetto: e.target.value })}
                  placeholder="Oggetto"
                  className="h-8 text-xs bg-white border-slate-300 text-slate-700 focus-visible:ring-0"
                />

                <Textarea
                  value={formData.messaggio}
                  onChange={(e) => setFormData({ ...formData, messaggio: e.target.value })}
                  placeholder="Messaggio *"
                  className="text-xs bg-white border-slate-300 text-slate-700 min-h-[72px] resize-none focus-visible:ring-0"
                />

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full h-8 text-xs bg-[#0F766E] hover:bg-[#0d6560] text-white"
                >
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  Invia richiesta
                </Button>
              </form>
            )}

            <button
              onClick={() => setFormOpen((v) => !v)}
              className="w-full h-9 rounded-lg text-xs font-semibold transition-colors border border-slate-300 text-slate-700 bg-white hover:bg-slate-100"
            >
              {formOpen ? "Chiudi" : "Contatta assistenza"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SidebarBrand({ collapsed = false }) {
  return (
    <div className={`shrink-0 border-b border-slate-200/80 pt-4 pb-3 ${collapsed ? "px-1.5" : "px-4"}`}>
      <Link to="/" className="flex h-12 items-center justify-center" data-testid="logo-link">
        <img
          src={collapsed ? LOGO_MARK_URL : LOGO_FULL_URL}
          alt="Affidya"
          className={collapsed ? "h-8 w-8 object-contain" : "h-12 w-auto max-w-full"}
        />
      </Link>
    </div>
  );
}

function SidebarNav({
  sections,
  collapsed = false,
  onNavigate,
  isItemActive,
  testIdPrefix = "sidebar-nav",
}) {
  return (
    <>
      {sections.map((section, sIdx) => {
        if (!section.items.length) return null;
        return (
          <div
            key={section.title ?? `sec-${sIdx}`}
            className={getNavSectionWrapperClass(section, sIdx, collapsed ? "collapsed" : "desktop")}
          >
            {section.title ? (
              <div
                className={
                  collapsed
                    ? "sr-only"
                    : `mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 ${
                        isCentroDiurnoNavSection(section) ? "px-1" : "px-3"
                      }`
                }
              >
                {section.title}
              </div>
            ) : null}
            {section.items.map((item) => {
              const active = isItemActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  title={item.label}
                  onClick={onNavigate}
                  data-testid={`${testIdPrefix}-${item.path.replace("/", "") || "dashboard"}`}
                  className={`mb-0.5 flex w-full items-center rounded-xl font-medium transition-all ${
                    collapsed
                      ? "h-10 justify-center"
                      : item.subItem
                        ? "gap-3 px-3 py-1.5 ml-4 text-xs"
                        : "gap-3 px-3 py-2 text-sm"
                  } ${
                    active
                      ? "bg-[#D4EFDC] text-emerald-900"
                      : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
                  }`}
                >
                  <item.icon
                    className={`shrink-0 ${
                      item.subItem && !collapsed ? "h-[15px] w-[15px]" : "h-[18px] w-[18px]"
                    } ${active ? "text-emerald-700" : "text-slate-400"}`}
                  />
                  <span className={collapsed ? "sr-only" : "truncate"}>{item.label}</span>
                  {!collapsed && item.badge ? (
                    <span className="ml-auto flex-shrink-0 rounded bg-sky-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                      {item.badge}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </div>
        );
      })}
    </>
  );
}

function SidebarChrome({
  sections,
  collapsed = false,
  onNavigate,
  isItemActive,
  token,
  user,
  testIdPrefix = "sidebar-nav",
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <SidebarBrand collapsed={collapsed} />
      <nav
        className={`sidebar-scroll flex-1 overflow-y-auto ${
          collapsed ? "px-2 pt-5 pb-2" : "px-2.5 pt-3 pb-2"
        }`}
      >
        <div className={collapsed ? "mb-2" : "mb-3"}>
          <StrutturaSwitcher variant="sidebar" collapsed={collapsed} />
        </div>
        <SidebarNav
          sections={sections}
          collapsed={collapsed}
          onNavigate={onNavigate}
          isItemActive={isItemActive}
          testIdPrefix={testIdPrefix}
        />
      </nav>
      {!collapsed ? <SidebarSupportWidget token={token} user={user} /> : null}
    </div>
  );
}

function LayoutShell({ children }) {
  const { user, logout, token, impersonating, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { header: pageHeader } = usePageHeader();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("struttura-sidebar-collapsed") === "true"
  );
  
  // Sezioni di navigazione filtrate in base ai permessi effettivi dell'utente
  const navSections = getNavSections(user, hasPermission);
  
  // Carica alerts da localStorage (per persistere le cancellazioni)
  const [alerts, setAlerts] = useState(() => {
    const dismissed = JSON.parse(localStorage.getItem('dismissedAlerts') || '[]');
    return INITIAL_ALERTS.filter(a => !dismissed.includes(a.id));
  });
  const [dismissedAlertTypes, setDismissedAlertTypes] = useState(() => {
    return JSON.parse(localStorage.getItem('dismissedAlertTypes') || '[]');
  });
  
  const [leadCount, setLeadCount] = useState(0);
  const [assistenzaUnread, setAssistenzaUnread] = useState(0);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchAssistenzaUnread = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/api/assistenza/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAssistenzaUnread(Number(response.data?.count) || 0);
      } catch (error) {
        console.error("Assistenza unread fetch error:", error);
      }
    };
    fetchAssistenzaUnread();
    const interval = setInterval(fetchAssistenzaUnread, 60000);
    const onUnreadChanged = (event) => {
      if (typeof event?.detail?.count === "number") {
        setAssistenzaUnread(event.detail.count);
      } else {
        fetchAssistenzaUnread();
      }
    };
    window.addEventListener("assistenza-unread-changed", onUnreadChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener("assistenza-unread-changed", onUnreadChanged);
    };
  }, [token, API_URL]);

  useEffect(() => {
    if (location.pathname === "/assistenza") {
      setAssistenzaUnread(0);
    }
  }, [location.pathname]);
  
  // Fetch lead compatibili per notifica
  useEffect(() => {
    const fetchLeadCompatibili = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/api/posti-liberi/lead-compatibili`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const count = response.data.count || 0;
        setLeadCount(count);
        
        if (count > 0 && !dismissedAlertTypes.includes('lead-compatibili')) {
          setAlerts(prevAlerts => {
            const existingLeadAlert = prevAlerts.find(a => a.type === "lead-compatibili");
            if (!existingLeadAlert) {
              return [{
                id: "lead-" + Date.now(),
                type: "lead-compatibili",
                icon: Users,
                title: `${count} ${count === 1 ? 'ospite compatibile' : 'ospiti compatibili'}!`,
                description: "Nuove richieste selezionate per la tua struttura",
                time: "Adesso",
                color: "text-amber-500",
                bgColor: "bg-amber-100",
                path: "/posti-liberi"
              }, ...prevAlerts];
            }
            return prevAlerts;
          });
        }
      } catch (error) {
        console.error("Lead compatibili fetch error:", error);
      }
    };
    fetchLeadCompatibili();
  }, [token, API_URL, dismissedAlertTypes]);

  useEffect(() => {
    const fetchInternalNotifications = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { unread_only: true, limit: 20 },
        });
        const items = response.data.items || [];
        setAlerts((prev) => {
          const withoutInternal = prev.filter((a) => !String(a.id).startsWith("notif-"));
          const notifAlerts = items.map((n) => ({
            id: `notif-${n.id}`,
            type: n.tipo || "notification",
            icon: Bell,
            title: n.titolo,
            description: n.messaggio,
            time: "Adesso",
            color: "text-emerald-600",
            bgColor: "bg-emerald-100",
            path: n.path || "/",
          }));
          return [...notifAlerts, ...withoutInternal];
        });
      } catch (error) {
        console.error("Notifications fetch error:", error);
      }
    };
    fetchInternalNotifications();
    const interval = setInterval(fetchInternalNotifications, 60000);
    return () => clearInterval(interval);
  }, [token, API_URL]);

  useEffect(() => {
    const fetchTerapieExtraAlerts = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API_URL}/api/terapie-extra/alerts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = response.data.items || [];
        const count = response.data.count ?? items.length;

        setAlerts((prevAlerts) => {
          const withoutTerapie = prevAlerts.filter((a) => a.type !== "terapie-extra-pending");
          if (count === 0 || dismissedAlertTypes.includes("terapie-extra-pending")) {
            return withoutTerapie;
          }
          const first = items[0];
          const overdueCount = items.filter((i) => i.stato === "overdue").length;
          return [
            {
              id: `terapie-extra-${first?.fascicolo_id || "org"}-${count}`,
              type: "terapie-extra-pending",
              icon: Pill,
              title: `${count} somministrazion${count === 1 ? "e" : "i"} terapie extra`,
              description:
                overdueCount > 0
                  ? `${overdueCount} in ritardo — ${first?.nome_ospite || "ospite"}`
                  : `Prossima: ${first?.nome_terapia || "terapia"} — ${first?.nome_ospite || "ospite"}`,
              time: "Adesso",
              color: overdueCount > 0 ? "text-red-500" : "text-amber-500",
              bgColor: overdueCount > 0 ? "bg-red-100" : "bg-amber-100",
              path: first?.fascicolo_id
                ? `/fascicoli-assistenziali/${first.fascicolo_id}?tab=sanitaria`
                : "/fascicoli-assistenziali",
            },
            ...withoutTerapie,
          ];
        });
      } catch (error) {
        console.error("Terapie extra alerts fetch error:", error);
      }
    };
    fetchTerapieExtraAlerts();
    const interval = setInterval(fetchTerapieExtraAlerts, 60000);
    return () => clearInterval(interval);
  }, [token, API_URL, dismissedAlertTypes]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleAlertClick = (alert) => {
    if (String(alert.id).startsWith("notif-") && token) {
      const notifId = String(alert.id).replace(/^notif-/, "");
      axios.post(
        `${API_URL}/api/notifications/mark-read`,
        { ids: [notifId] },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {});
    }
    setAlerts(prevAlerts => prevAlerts.filter(a => a.id !== alert.id));
    
    // Salva che questo tipo di notifica è stato cancellato
    if (alert.type) {
      const newDismissed = [...dismissedAlertTypes, alert.type];
      setDismissedAlertTypes(newDismissed);
      localStorage.setItem('dismissedAlertTypes', JSON.stringify(newDismissed));
    }
    
    // Salva l'ID cancellato per le notifiche statiche
    const dismissedIds = JSON.parse(localStorage.getItem('dismissedAlerts') || '[]');
    if (!dismissedIds.includes(alert.id)) {
      dismissedIds.push(alert.id);
      localStorage.setItem('dismissedAlerts', JSON.stringify(dismissedIds));
    }
    
    // Naviga alla pagina correlata
    if (alert.path) {
      navigate(alert.path);
    }
  };

  const handleClearAllAlerts = () => {
    // Salva tutti i tipi di notifica come cancellati
    const types = alerts.map(a => a.type).filter(Boolean);
    const newDismissed = [...new Set([...dismissedAlertTypes, ...types])];
    setDismissedAlertTypes(newDismissed);
    localStorage.setItem('dismissedAlertTypes', JSON.stringify(newDismissed));
    
    // Salva tutti gli ID
    const dismissedIds = JSON.parse(localStorage.getItem('dismissedAlerts') || '[]');
    const allIds = [...new Set([...dismissedIds, ...alerts.map(a => a.id)])];
    localStorage.setItem('dismissedAlerts', JSON.stringify(allIds));
    
    setAlerts([]);
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const isNavItemActive = (path) => {
    if (path === "/") return location.pathname === "/";
    if (path === "/centro-diurno") return location.pathname === "/centro-diurno";
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  useEffect(() => {
    localStorage.setItem("struttura-sidebar-collapsed", String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const toggleSidebar = () => setSidebarCollapsed((current) => !current);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-[#D9E0EA] text-slate-900">
      <ThemeChoiceDialog />
      {impersonating && (
        <div className="bg-amber-500 text-white text-center text-sm font-medium py-1.5 px-4 flex items-center justify-center gap-2 relative z-[60]">
          <AlertTriangle className="h-4 w-4" />
          Stai visualizzando come: {user?.name || user?.nome || user?.email}
          <button
            onClick={() => { window.close(); }}
            className="ml-4 underline hover:no-underline text-white/90 hover:text-white"
          >
            Chiudi scheda
          </button>
        </div>
      )}
      {!impersonating && user?.account_status === "trial_active" && user?.trial_expires_at && (
        <div className="bg-emerald-700 text-white text-center text-xs sm:text-sm font-medium py-2 px-4 flex flex-wrap items-center justify-center gap-2 relative z-[60] leading-snug">
          <Info className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
          <span>
            Prova gratuita attiva — scade il{" "}
            {new Date(user.trial_expires_at).toLocaleDateString("it-IT", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
          <Link to="/upgrade" className="underline hover:no-underline text-white/90 hover:text-white">
            Effettua upgrade
          </Link>
        </div>
      )}
      {!impersonating && user?.is_demo_account && (
        <div className="bg-[#0f766e] text-white text-center text-xs sm:text-sm font-medium py-2 px-4 flex flex-wrap items-center justify-center gap-2 relative z-[60] leading-snug">
          <Info className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
          <span>
            Ambiente demo condiviso: non inserire dati sensibili o personali reali — altri utenti potrebbero visualizzarli.
          </span>
        </div>
      )}

      {mobileMenuOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/40"
            aria-label="Chiudi menu"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="absolute left-0 top-0 flex h-full w-72 max-w-[85vw] flex-col bg-[#EEF0F3] shadow-xl">
            <button
              type="button"
              className="absolute right-3 top-4 z-10 rounded-lg p-1 text-slate-500 hover:bg-white/80"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Chiudi"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarChrome
              sections={navSections}
              onNavigate={() => setMobileMenuOpen(false)}
              isItemActive={isNavItemActive}
              token={token}
              user={user}
              testIdPrefix="mobile-nav"
            />
          </aside>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 p-3 md:p-4">
        <div className="flex min-h-0 w-full gap-[9px] md:gap-3">
          <aside
            className={`relative z-20 hidden shrink-0 flex-col transition-[width] duration-200 lg:flex ${
              sidebarCollapsed ? "w-[68px]" : "w-64"
            }`}
          >
            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-tl-2xl rounded-bl-2xl rounded-tr-none rounded-br-none bg-[#EEF0F3] shadow-[0_2px_8px_rgba(15,23,42,0.08)]">
              <SidebarChrome
                sections={navSections}
                collapsed={sidebarCollapsed}
                isItemActive={isNavItemActive}
                token={token}
                user={user}
              />
            </div>
            <div className="absolute right-0 top-[76px] z-30 flex h-9 w-9 translate-x-[12px] -translate-y-1/2 items-center justify-center">
              <span className="pointer-events-none absolute h-9 w-9 rounded-full bg-[#D9E0EA]" aria-hidden />
              <button
                type="button"
                onClick={toggleSidebar}
                className="relative flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm transition hover:border-emerald-600 hover:bg-emerald-600 hover:text-white"
                aria-label={sidebarCollapsed ? "Espandi menu" : "Riduci menu"}
                title={sidebarCollapsed ? "Espandi menu" : "Riduci menu"}
                aria-expanded={!sidebarCollapsed}
              >
                {sidebarCollapsed ? (
                  <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                ) : (
                  <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
                )}
              </button>
            </div>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] lg:rounded-tl-none lg:rounded-bl-none">
            <header className="shrink-0 border-b border-slate-100">
              <div className="flex min-h-16 flex-wrap items-center gap-3 px-4 md:px-6">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-10 w-10 rounded-xl p-0 lg:hidden"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label="Apri menu"
                  data-testid="mobile-menu-toggle"
                >
                  <Menu className="h-5 w-5" />
                </Button>

                {pageHeader?.title ? (
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    {pageHeader.Icon ? (
                      <div
                        className={`hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-sm sm:flex ${
                          pageHeader.iconContainerClassName || "bg-slate-100"
                        }`}
                      >
                        <pageHeader.Icon
                          className={`h-5 w-5 ${pageHeader.iconClassName || "text-slate-700"}`}
                        />
                      </div>
                    ) : null}
                    <div className="flex min-w-0 flex-1 items-center gap-4">
                      <div className="min-w-0">
                        <h1 className="truncate text-xl font-semibold leading-tight tracking-tight text-slate-900 md:text-2xl">
                          {pageHeader.title}
                        </h1>
                        {pageHeader.subtitle ? (
                          <p className="mt-0.5 truncate text-sm text-slate-500">
                            {pageHeader.subtitle}
                          </p>
                        ) : null}
                      </div>
                      {pageHeader.actions ? (
                        <div className="ml-auto flex shrink-0 items-center">{pageHeader.actions}</div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="min-w-0 flex-1" />
                )}

                <div className="ml-auto flex items-center gap-1 md:gap-2">
              {/* Badge aggiornamenti assistenza */}
              {assistenzaUnread > 0 ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 relative"
                      data-testid="assistenza-updates-toggle"
                      aria-label="Aggiornamenti assistenza"
                    >
                      <span className="absolute inset-0 rounded-full bg-teal-400/30 animate-ping" />
                      <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 shadow-sm shadow-teal-500/30">
                        <Headphones className="w-4 h-4 text-white" />
                      </span>
                      <Badge className="absolute -top-0.5 -right-0.5 h-5 min-w-[1.25rem] px-1 flex items-center justify-center bg-amber-400 text-amber-950 text-[10px] font-bold border-2 border-white shadow-sm animate-pulse">
                        {assistenzaUnread > 9 ? "9+" : assistenzaUnread}
                      </Badge>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <div className="px-3 py-3 border-b">
                      <p className="text-sm font-semibold text-slate-900">Assistenza</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {assistenzaUnread === 1
                          ? "Hai 1 ticket con una nuova risposta o un cambio di stato."
                          : `Hai ${assistenzaUnread} ticket con nuove risposte o cambi di stato.`}
                      </p>
                    </div>
                    <Link to="/assistenza" onClick={() => setAssistenzaUnread(0)}>
                      <DropdownMenuItem
                        className="cursor-pointer py-3"
                        data-testid="assistenza-updates-go"
                      >
                        <Headphones className="w-4 h-4 mr-2 text-teal-600" />
                        Vai alle richieste di assistenza
                      </DropdownMenuItem>
                    </Link>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}

              {/* Alerts Bell */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10 relative"
                    data-testid="alerts-toggle"
                  >
                    <Bell className="w-5 h-5 text-slate-500" />
                    {alerts.length > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-[10px] font-bold">
                        {alerts.length}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80">
                  <div className="px-3 py-2 border-b flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Notifiche</p>
                      <p className="text-xs text-slate-500">Clicca per visualizzare e rimuovere</p>
                    </div>
                    {alerts.length > 0 && (
                      <button 
                        onClick={handleClearAllAlerts}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Cancella tutte
                      </button>
                    )}
                  </div>
                  <ScrollArea className="h-80">
                    <div className="p-2">
                      {alerts.length === 0 ? (
                        <div className="py-8 text-center">
                          <Bell className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-500">Nessuna notifica</p>
                        </div>
                      ) : (
                        alerts.map((alert) => (
                          <div 
                            key={alert.id} 
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors group"
                            onClick={() => handleAlertClick(alert)}
                            data-testid={`alert-${alert.id}`}
                          >
                            <div className={`w-9 h-9 rounded-lg ${alert.bgColor} flex items-center justify-center flex-shrink-0`}>
                              <alert.icon className={`w-4 h-4 ${alert.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 group-hover:text-[#4CAF50]">{alert.title}</p>
                              <p className="text-xs text-slate-500 truncate">{alert.description}</p>
                              <p className="text-[10px] text-slate-400 mt-1">{alert.time}</p>
                            </div>
                            <X className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>

              <ThemeToggle className="h-10 w-10" />

              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 h-10 px-2 md:px-3"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#4CAF50] flex items-center justify-center text-white text-sm font-semibold">
                      {getInitials(user?.name)}
                    </div>
                    <span className="hidden md:inline text-sm font-medium text-slate-700">
                      {user?.name?.split(" ")[0] || "Utente"}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <Link to="/profilo-struttura">
                    <DropdownMenuItem data-testid="menu-profilo-struttura">
                      <Home className="w-4 h-4 mr-2" />
                      Profilo Struttura
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/assistenza" onClick={() => setAssistenzaUnread(0)}>
                    <DropdownMenuItem data-testid="menu-assistenza" className="relative">
                      <Headphones className="w-4 h-4 mr-2" />
                      Assistenza
                      {assistenzaUnread > 0 ? (
                        <Badge className="ml-auto h-5 min-w-[1.25rem] px-1.5 bg-teal-600 text-white text-[10px] font-bold animate-pulse">
                          {assistenzaUnread > 9 ? "9+" : assistenzaUnread}
                        </Badge>
                      ) : null}
                    </DropdownMenuItem>
                  </Link>
                  <a href="/documents/MANUALE_UTENTE_AFFIDYA.html" target="_blank" rel="noopener noreferrer">
                    <DropdownMenuItem data-testid="menu-manuale">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Manuale
                    </DropdownMenuItem>
                  </a>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
                </div>
              </div>
            </header>

            <main className="page-transition min-h-0 flex-1 overflow-auto">
              {children}
            </main>
          </div>
        </div>
      </div>
      
      {/* Profile Modal */}
      <ProfileModal open={profileModalOpen} onOpenChange={setProfileModalOpen} />

      {/* Assistente virtuale */}
      <AssistantChatWidget />

      {/* Prompt notifiche push (dopo login) */}
      <PushNotificationPrompt token={token} />
    </div>
  );
}

export default function Layout({ children }) {
  return (
    <PageHeaderProvider>
      <TermsReacceptanceGate>
        <LayoutShell>{children}</LayoutShell>
      </TermsReacceptanceGate>
    </PageHeaderProvider>
  );
}
